/**
 * ModeSelector — 3-chip selector for upload report mode.
 * Standard (general) | Consultant | Contractor.
 *
 * Controlled component. Parent owns the value and onChange. Default selection
 * is supplied by the parent (UploadZone seeds with 'general').
 *
 * Accessibility:
 *   - role="group" + aria-label="Report mode" on the wrapper
 *   - aria-pressed reflects active state on each chip
 *   - Roving tabindex: only the active chip is tab-reachable; ArrowLeft and
 *     ArrowRight move focus among chips (wrap-around). Space and Enter
 *     activate the focused chip via the default button onClick.
 */
import { useRef } from 'react';

const MODES = [
  { value: 'general', label: 'Standard' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'contractor', label: 'Contractor' },
];

export function ModeSelector({ value, onChange }) {
  const btnRefs = useRef([]);

  const handleKeyDown = (e, idx) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (idx - 1 + MODES.length) % MODES.length;
      btnRefs.current[prev]?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (idx + 1) % MODES.length;
      btnRefs.current[next]?.focus();
    }
  };

  return (
    <div className="mode-selector">
      <div className="mode-selector-label" aria-hidden="true">REPORT MODE</div>
      <div className="mode-chip-group" role="group" aria-label="Report mode">
        {MODES.map((m, idx) => {
          const isActive = value === m.value;
          return (
            <button
              key={m.value}
              ref={(el) => { btnRefs.current[idx] = el; }}
              type="button"
              className={`mode-chip ${isActive ? 'active' : 'inactive'}`}
              aria-pressed={isActive ? 'true' : 'false'}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(m.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
